<template>
  <div>
    <div v-if="!streamAlive && !isLoading" class="text-center">
      <h3 class="color-light text-s-xl">Stream is offline</h3>
      <span class="text-dark-6 text-s-m">To see the analytics of previous streams, go to
        <a class="text-dark-6 text-underline cursor-pointer" :to="{ name: 'StreamsManageAnalyticsStreamSessions' }">Stream Sessions
          History</a>.</span>
    </div>

    <Spinner v-if="isLoading" text="Retrieving data..." classes="text-dark-8 mt-5" spinner-color="var(--c-dark-8)"
      spinner-size="15px" />

    <div v-else-if="streamAlive && liveSession" class="flex flex-wrap items-center gap-x-8 gap-y-3 mb-5">
      <div class="flex items-center gap-2">
        <span class="text-sm text-surface-8">Stream Started On</span>
        <span class="text-sm text-white font-medium">{{ formatTime(liveSession.started_at) }}</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-sm text-surface-8">Status</span>
        <span v-if="liveSession.live" class="inline-flex items-center gap-1.5 text-sm text-success capitalize">
          <span class="size-2 rounded-full bg-success"></span>
          {{ liveStream.broadcasting_status }}
        </span>
        <span v-else class="text-sm text-surface-9">Finished</span>
      </div>
      <TabGroup type="segmented" class="ml-auto">
        <TabItem v-for="range in RANGE_PRESETS" :key="range.ms"
          :is-active="selectedRangeMs === range.ms"
          @click="selectedRangeMs = range.ms">
          {{ range.label }}
        </TabItem>
      </TabGroup>
    </div>

    <!-- actual charts -->
    <div id="chartContainer">
      <section class="grid grid-cols-1 lg:grid-cols-2 gap-4" v-if="!isLoading && streamAlive && liveSession">
        <!-- <Card class="metric-chart">
          <div class="chart-wrapper">
            <div class="flex justify-between">
              <p class="mb-1"><span class="text-s-m text-dark-7">Delay</span> <span class="text-white text-s-xxl">{{
                currDelay }} ms</span></p>
              <Tooltip :content="chartControlMsg"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd"
                  d="M8 1.75C4.54822 1.75 1.75 4.54822 1.75 8C1.75 11.4518 4.54822 14.25 8 14.25C11.4518 14.25 14.25 11.4518 14.25 8C14.25 4.54822 11.4518 1.75 8 1.75ZM0.25 8C0.25 3.71979 3.71979 0.25 8 0.25C12.2802 0.25 15.75 3.71979 15.75 8C15.75 12.2802 12.2802 15.75 8 15.75C3.71979 15.75 0.25 12.2802 0.25 8ZM9 5C9 5.55228 8.55228 6 8 6C7.44772 6 7 5.55228 7 5C7 4.44772 7.44772 4 8 4C8.55228 4 9 4.44772 9 5ZM8.75 8.5C8.75 8.08579 8.41421 7.75 8 7.75C7.58579 7.75 7.25 8.08579 7.25 8.5V11.5C7.25 11.9142 7.58579 12.25 8 12.25C8.41421 12.25 8.75 11.9142 8.75 11.5V8.5Z"
                  fill="#68759C" />
</svg></Tooltip>
            </div>
            <p class="text-s-s text-dark-6 mb-2">The ideal delay is under 1000 ms. Any unusual hike means our server
              doesn't receive input from your end on time. <a target="_blank"
                href="https://docs.castr.com/en/articles/6553493-understanding-health-charts-of-your-stream"
                class="text-dark-9 text-underline">Learn more</a></p>
            <input-health-chart ref="delayChart" :chart-data="delayData" label="Delay" />
          </div>
        </Card> -->
        <Card class="metric-chart lg:col-span-2">
          <div class="chart-wrapper">
            <div class="flex justify-between">
              <div class="mb-2 flex flex-wrap gap-x-12 gap-y-3">
                <div>
                  <p class="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-dark-7 mb-1">
                    <span class="size-2 rounded-full bg-[#2D68FF]"></span>
                    Video Bitrate
                  </p>
                  <p class="text-white text-s-xxl leading-none mb-1.5">
                    {{ formatNumber(liveVidBitrate) }}
                    <span class="text-s-s text-dark-7">Kbps</span>
                  </p>
                  <p class="text-s-s text-dark-6 mb-0">
                    min {{ formatNumber(videoStats.min) }} · avg {{ formatNumber(videoStats.avg) }} · max {{ formatNumber(videoStats.max) }}
                  </p>
                </div>
                <div>
                  <p class="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-dark-7 mb-1">
                    <span class="size-2 rounded-full bg-[#34D399]"></span>
                    Audio Bitrate
                  </p>
                  <p class="text-white text-s-xxl leading-none mb-1.5">
                    {{ formatNumber(liveAudioBitrate) }}
                    <span class="text-s-s text-dark-7">Kbps</span>
                  </p>
                  <p class="text-s-s text-dark-6 mb-0">
                    min {{ formatNumber(audioStats.min) }} · avg {{ formatNumber(audioStats.avg) }} · max {{ formatNumber(audioStats.max) }}
                  </p>
                </div>
              </div>
              <Tooltip :content="chartControlMsg"><icon-base class="text-[#68759C]" :width="16" :height="16"><icon-info /></icon-base></Tooltip>
            </div>
            <p class="text-s-s text-dark-6 mb-2">Red segments mark video drops below half its average. <a target="_blank"
                href="https://docs.castr.com/en/articles/6553493-understanding-health-charts-of-your-stream"
                class="text-dark-9 text-underline">Learn more</a></p>
            <input-health-chart ref="bitrateChart" :chart-data="bitrateData" label="Bitrate" />
          </div>
        </Card>
        <Card class="metric-chart lg:col-span-2">
          <div class="chart-wrapper">
            <div class="flex justify-between">
              <p class="mb-1"><span class="text-s-m text-dark-7">Frame Rate</span> <span class="text-white text-s-xxl">{{
                currFps }} fps</span></p>
              <Tooltip :content="chartControlMsg"><icon-base class="text-[#68759C]" :width="16" :height="16"><icon-info /></icon-base></Tooltip>
            </div>
            <p class="text-s-s text-dark-6 mb-2">Frames per second received from your encoder. A steady FPS is healthy —
              dips usually point to encoder overload or network congestion. <a target="_blank"
                href="https://docs.castr.com/en/articles/6553493-understanding-health-charts-of-your-stream"
                class="text-dark-9 text-underline">Learn more</a></p>
            <input-health-chart ref="fpsChart" :chart-data="frameRateData" label="FPS" />
          </div>
        </Card>
      </section>
    </div>
  </div>
</template>

<script>
import Tooltip from '@/components/Atoms/Tooltip.vue'
import _ from "lodash";
import moment from "moment";
import InputHealthChart from "@/components/ui/InputHealthChart.vue";
import StreamStatsService from "@/services/StreamStatsService";
import { mapGetters } from 'vuex';
import Spinner from "@/components/ui/Spinner.vue";
import Card from '@/components/Atoms/Card.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconInfo from '@/components/icon/IconInfo.vue'

const MAX_HISTORICAL_DATA_LIMIT = 920;
// A break is drawn in the charts when samples are further apart than this —
// making encoder dropouts visible instead of silently bridging the gap.
const GAP_MS = 30 * 1000;
const RANGE_PRESETS = [
  { label: '5m', ms: 5 * 60 * 1000 },
  { label: '15m', ms: 15 * 60 * 1000 },
  { label: '1h', ms: 60 * 60 * 1000 },
];

export default {
  name: "StreamsManageAnalyticsLiveInputHealthCharts",
  components: {
		Tooltip, InputHealthChart, Spinner, Card, FormButton, TabGroup, TabItem, IconBase, IconInfo },
  props: {
    stream: {
      type: Object,
      required: true,
      validator(v) {
        return Object.prototype.hasOwnProperty.call(v, "_id");
      },
    },
    streamAlive: {
      type: Boolean,
    },
    // Live media-server pulse shared with the top overview bar. Used for the
    // header's "current" readout so the two never disagree; the plotted line
    // and min/avg/max stay sourced from the aggregated session data.
    mediaPulse: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      RANGE_PRESETS,
      selectedRangeMs: RANGE_PRESETS[1].ms,
      liveStream: null,
      isLoading: true,
      xScale: [],
      currDelay: 0,
      currVidBitrate: 0,
      currAudioBitrate: 0,
      currFps: 0,
      lastTime: 0,
      historicalData: [],
      historicalLowData: [],
      currentSessionId: null,
      liveSession: null,
    };
  },
  computed: {
    // Header "current" values track the live media pulse (same source as the
    // top overview bar) and fall back to the latest session point if the pulse
    // hasn't arrived yet.
    liveVidBitrate() {
      const pulseVal = this.mediaPulse?.bitrate;
      return (pulseVal || pulseVal === 0) && this.mediaPulse?.alive ? pulseVal : this.currVidBitrate;
    },
    liveAudioBitrate() {
      const pulseVal = this.mediaPulse?.audioBitrate;
      return (pulseVal || pulseVal === 0) && this.mediaPulse?.alive ? pulseVal : this.currAudioBitrate;
    },
    // Slice of the loaded history matching the selected time-range preset.
    windowedData() {
      if (!this.historicalData.length) return [];
      const startTime = this.lastTime - this.selectedRangeMs;
      return this.historicalData.filter((d) => d.time_ms >= startTime);
    },
    videoStats() {
      return this.computeStats(this.windowedData.map((d) => d.video_bitrate));
    },
    audioStats() {
      return this.computeStats(this.windowedData.map((d) => d.audio_bitrate));
    },
    delayData() {
      let data = [];
      this.windowedData.forEach((d) => {
        data.push({ x: d.time_ms, y: d.ts_delay });
      });
      return {
        labels: this.xScale,
        datasets: [
          {
            fill: true,
            data: data,
            borderColor: "rgba(45, 104, 255, 1)",
            borderWidth: 2,
            pointRadius: 0.3,
            backgroundColor: "rgba(45, 104, 255, 0.03)",
          },
        ],
      };
    },
    // Video and audio bitrate share one full-width chart: video dominates
    // vertical space (it's the volatile signal operators watch), while audio
    // rides on its own right-hand axis with 2x headroom so the two lines
    // occupy separate visual lanes. Legend items toggle each series.
    bitrateData() {
      const videoData = [];
      const audioData = [];
      this.windowedData.forEach((d) => {
        videoData.push({ x: d.time_ms, y: d.video_bitrate });
        audioData.push({ x: d.time_ms, y: d.audio_bitrate });
      });

      // Flag segments where video collapses below half its window average.
      const videoAvg = this.videoStats.avg;
      const videoZones = videoAvg
        ? [
            { value: videoAvg * 0.5, color: "rgba(248, 113, 113, 1)" },
            { color: "rgba(45, 104, 255, 1)" },
          ]
        : undefined;

      // Park the audio line in the lower half of the chart.
      const audioMax = this.audioStats.max;
      const audioAxisMax = audioMax ? Math.ceil((audioMax * 2) / 50) * 50 : undefined;

      return {
        labels: this.xScale,
        datasets: [
          {
            name: "Video Bitrate (Kbps)",
            fill: true,
            data: videoData,
            borderColor: "rgba(45, 104, 255, 1)",
            borderWidth: 2,
            pointRadius: 0.3,
            backgroundColor: "rgba(45, 104, 255, 0.03)",
            zones: videoZones,
            gapMs: GAP_MS,
          },
          {
            name: "Audio Bitrate (Kbps)",
            fill: true,
            data: audioData,
            borderColor: "rgba(52, 211, 153, 0.9)",
            borderWidth: 2,
            pointRadius: 0.3,
            backgroundColor: "rgba(52, 211, 153, 0.03)",
            yAxisIndex: 1,
            yAxisMax: audioAxisMax,
            gapMs: GAP_MS,
          },
        ],
      };
    },
    frameRateData() {
      let data = [];
      this.windowedData.forEach((d) => {
        data.push({ x: d.time_ms, y: d.fps });
      });
      return {
        labels: this.xScale,
        datasets: [
          {
            fill: true,
            data,
            borderColor: "rgba(45, 104, 255, 1)",
            borderWidth: 2,
            pointRadius: 0.3,
            backgroundColor: "rgba(45, 104, 255, 0.03)",
            gapMs: GAP_MS,
          },
        ],
      };
    },
    chartControlMsg() {
      return `Drag to zoom into a time range, or hold Ctrl (Cmd on Mac) and scroll. Click a legend item to show or hide a series.`
    },
  },
  async created() {
    await this.initializeCharts();
  },
  mounted() {
    document.getElementById('chartContainer').addEventListener('wheel', this.onMouseWheel, { capture: true, passive: false });
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  },
  beforeUnmount() {
    if (this.statsSubId) clearInterval(this.statsSubId);
    document.getElementById('chartContainer')?.removeEventListener('wheel', this.onMouseWheel);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    onMouseWheel(event) {
      if (!event.ctrlKey && !event.metaKey) {
        event.stopPropagation();
      }
    },
    formatNumber(value) {
      return (value || 0).toLocaleString('en-US');
    },
    computeStats(values) {
      const nums = values.filter((v) => typeof v === "number" && !Number.isNaN(v));
      if (!nums.length) return { min: 0, avg: 0, max: 0 };
      const sum = nums.reduce((acc, v) => acc + v, 0);
      // Exclude zeros so "min" reflects the lowest active bitrate rather than a
      // dropout/edge sample (dropouts still show on the line itself).
      const positives = nums.filter((v) => v > 0);
      return {
        min: Math.round(positives.length ? Math.min(...positives) : 0),
        avg: Math.round(sum / nums.length),
        max: Math.round(Math.max(...nums)),
      };
    },
    async initializeCharts(showLoader = true) {
      if (showLoader) this.isLoading = true;
      this.liveStream = this.$props.stream;
      const now = Date.now();
      const aggInterval = 60 * 1000;
      this.lastTime = now - (now % aggInterval);
      if (this.stream.key) {
        // load historical data
        let sessionsList = await StreamStatsService.getSessionsList(
          this.stream.key
        );
        if(!_.isArray(sessionsList)) {
          sessionsList = [];
        }
        const liveSession = sessionsList.find((session) => session.live);
        this.liveSession = liveSession;

        if (liveSession) {
          this.liveStream.broadcasting_status = 'online';
          this.currentSessionId = liveSession.id;
          this.historicalData = await StreamStatsService.getSessionData(
            liveSession.id,
            MAX_HISTORICAL_DATA_LIMIT,
          );
          if (this.historicalData.length) {
            this.historicalData = this.historicalData.reverse();
            const lastDataPoint = this.historicalData[
              this.historicalData.length - 1
            ];
            this.lastTime = lastDataPoint.time_ms;
            this.currDelay = lastDataPoint.ts_delay || 0;
            this.currVidBitrate = lastDataPoint.video_bitrate || 0;
            this.currAudioBitrate = lastDataPoint.audio_bitrate || 0;
            this.currFps = lastDataPoint.fps || 0;
            // The full loaded history stays in memory; `windowedData` slices
            // it per the selected time-range preset (5m/15m/1h).
          }
        }
      }

      this.subscribeToStats();

      this.xScale = [];

      if (this.historicalData?.length < 10) {
        const currAggTime = now - (now % aggInterval);
        this.lastAggInterval = currAggTime;

        for (let index = 0; index < 10; index++) {
          this.xScale.push(new Date(currAggTime + index * aggInterval));
        }

        this.lastTime = this.historicalData?.[0]?.time
          ? new Date(this.historicalData[0].time).getTime() + 10 * aggInterval
          : currAggTime + 10 * aggInterval;
      }

      if (showLoader) this.isLoading = false;
    },
    subscribeToStats() {
      // this.fetchStatsData();
      if (this.statsSubId) clearInterval(this.statsSubId);
      // Don't burn CPU/network polling a backgrounded tab; the loop resumes
      // (and backfills) on visibility change.
      if (document.hidden) return;
      this.statsSubId = setInterval(() => {
        this.fetchStatsData();
      }, 5000);
    },
    onVisibilityChange() {
      if (document.hidden) {
        if (this.statsSubId) {
          clearInterval(this.statsSubId);
          this.statsSubId = null;
        }
        return;
      }
      // Back in view: reload the full session history so anything recorded
      // while hidden is filled in, then resume the live loop. Monitoring
      // users must never see a gap on return.
      this.refreshHistory().finally(() => this.subscribeToStats());
    },
    async refreshHistory() {
      if (!this.currentSessionId) {
        this.fetchStatsData();
        return;
      }
      try {
        let data = await StreamStatsService.getSessionData(
          this.currentSessionId,
          MAX_HISTORICAL_DATA_LIMIT,
        );
        if (Array.isArray(data) && data.length) {
          data = data.reverse();
          this.historicalData = data;
          const last = data[data.length - 1];
          this.lastTime = last.time_ms;
          this.currDelay = last.ts_delay || 0;
          this.currVidBitrate = last.video_bitrate || 0;
          this.currAudioBitrate = last.audio_bitrate || 0;
          this.currFps = last.fps || 0;
        }
      } catch (e) {
        // Fall back to a normal incremental fetch on error.
        this.fetchStatsData();
      }
    },
    async fetchStatsData() {
      try {
        if (!this.currentSessionId) {
          const pulseObject = await StreamStatsService.getStreamMediaPulse(
            this.stream.key,
            true
          );

          if ("success" in pulseObject && !pulseObject.success) {
            this.liveStream.broadcasting_status = "offline";
            return;
          }

          if (this.liveStream.broadcasting_status === "offline") {
            // stream has started
            this.liveStream.broadcasting_status = "online";
            return this.initializeCharts(false);
          }
        }
        let newData = null;

        try {
          newData = await StreamStatsService.getSessionData(
            this.currentSessionId,
            10
          );
        } catch {
          this.liveStream.broadcasting_status = "offline";
          return;
        }

        this.liveStream.broadcasting_status = "online";
        newData = newData.reverse();
        this.addNewData(newData);
      } catch (e) {
        console.log(e);
      }
    },
    addNewData(newData) {
      if (!newData?.length) return;
      const len = Math.max(0, this.historicalData.length - 1);
      if (len === 0) return this.historicalData.push(...newData);

      const lastHistoricalDataPoint = this.historicalData[len];
      for (const point of newData) {
        const time = point.time_ms;
        if (time > lastHistoricalDataPoint.time_ms) {
          this.historicalData.push(point);
          if (this.historicalData.length > MAX_HISTORICAL_DATA_LIMIT) this.historicalData.shift()
        }
      }

      const newLastPoint = newData[newData?.length - 1];
      this.lastTime = newLastPoint.time_ms;
      this.currDelay = newLastPoint.ts_delay || 0;
      this.currVidBitrate = newLastPoint.video_bitrate || 0;
      this.currAudioBitrate = newLastPoint.audio_bitrate || 0;
      this.currFps = newLastPoint.fps || 0;
    },
    formatTime(time) {
      return moment.utc(time).local()
        .format("MMM DD, h:mm:ss A");
    },
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #232d4d5a;
  border-color: #232d4d5a !important;
}

.metric-chart {
  background: var(--surface-1);
  padding: 1rem;
  border-radius: 0.75rem;
}
</style>
